// Clientes Suscripciones - Gestión de suscripciones con datos reales de Stripe
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientLayout } from '@/components/Layout/ClientLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/common/EmptyState';
import { useAuth } from '@/providers/AuthProvider';
import { clienteService } from '@/services/clienteService';
import { useClienteFichaQuery } from '@/hooks/useClienteExpediente';
import {
  CreditCardIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const STRIPE_DOMAIN = 'billing.stripe.com';

export function ClientesSuscripciones() {
  const { user } = useAuth();
  const { t } = useTranslation('portal');
  const { data: ficha, isLoading } = useClienteFichaQuery();
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  // Resumen de pagos activos desde ficha
  const paymentSummary = useMemo(() => {
    const payments = ficha?.billing_payments ?? [];
    const totalPaid = payments
      .filter(p => p.status === 'paid' || p.status === 'succeeded')
      .reduce((sum, p) => sum + p.amount_cents, 0);
    const pendingCount = payments.filter(p => p.status === 'open' || p.status === 'pending').length;
    return { totalPayments: payments.length, totalPaid, pendingCount };
  }, [ficha?.billing_payments]);

  const hasContracts = (ficha?.contract_mandates?.length ?? 0) > 0;

  const handleManageSubscriptions = useCallback(async () => {
    if (billingPortalLoading) return;
    setBillingPortalLoading(true);
    setPortalError(null);
    try {
      const response = await clienteService.createBillingPortalSession();
      // Validar que la URL sea de Stripe
      try {
        const url = new URL(response.url);
        if (!url.hostname.endsWith(STRIPE_DOMAIN) && !url.hostname.endsWith('stripe.com')) {
          setPortalError(t('subscriptions.portalError'));
          return;
        }
      } catch {
        setPortalError(t('subscriptions.portalError'));
        return;
      }
      window.location.href = response.url;
    } catch {
      setPortalError(t('subscriptions.portalError'));
    } finally {
      setBillingPortalLoading(false);
    }
  }, [t, billingPortalLoading]);

  if (isLoading) {
    return (
      <ClientLayout>
        <div className="space-y-6">
          <div>
            <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            <div className="h-4 w-64 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mt-2" />
          </div>
          <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('subscriptions.title')}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('subscriptions.subtitle')}
          </p>
        </div>

        {/* Información del usuario y billing */}
        <Card>
          <CardHeader>
            <CardTitle>{t('subscriptions.billingInfo')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">{t('subscriptions.billingEmail')}</label>
                <div className="text-gray-900 dark:text-gray-100 font-medium">{user?.email || 'N/A'}</div>
              </div>
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">{t('subscriptions.fullName')}</label>
                <div className="text-gray-900 dark:text-gray-100 font-medium">
                  {user?.full_name || t('common.notSpecified')}
                </div>
              </div>
            </div>

            {/* Resumen de pagos */}
            {paymentSummary.totalPayments > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{paymentSummary.totalPayments}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('subscriptions.totalPayments', 'Pagos totales')}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(paymentSummary.totalPaid / 100)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('subscriptions.totalPaid', 'Total pagado')}</p>
                </div>
                {paymentSummary.pendingCount > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-950 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{paymentSummary.pendingCount}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('subscriptions.pendingPayments', 'Pendientes')}</p>
                  </div>
                )}
              </div>
            )}

            {/* Error del portal */}
            {portalError && (
              <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 p-3 flex items-start gap-2" role="alert">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-red-700 dark:text-red-300">{portalError}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPortalError(null)}
                    className="text-red-600 dark:text-red-400 mt-1 p-0 h-auto"
                  >
                    {t('common.dismiss', 'Cerrar')}
                  </Button>
                </div>
              </div>
            )}

            <Button
              onClick={handleManageSubscriptions}
              disabled={billingPortalLoading}
              aria-busy={billingPortalLoading}
            >
              {billingPortalLoading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  {t('subscriptions.opening')}
                </>
              ) : (
                <>
                  <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                  {t('subscriptions.manageButton')}
                </>
              )}
            </Button>

            <div className="text-xs text-gray-500 dark:text-gray-400">
              <p>{t('subscriptions.stripeRedirectInfo')}</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>{t('subscriptions.stripeFeature1')}</li>
                <li>{t('subscriptions.stripeFeature2')}</li>
                <li>{t('subscriptions.stripeFeature3')}</li>
                <li>{t('subscriptions.stripeFeature4')}</li>
                <li>{t('subscriptions.stripeFeature5')}</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Contratos activos */}
        {hasContracts ? (
          <Card>
            <CardHeader>
              <CardTitle>{t('subscriptions.activeContracts', 'Contratos activos')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {ficha?.contract_mandates.map((cm) => (
                <div
                  key={cm.hiring_code}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {cm.service_name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('subscriptions.code', 'Código')}: {cm.hiring_code}
                    </p>
                  </div>
                  <Badge variant={cm.status === 'active' || cm.status === 'signed' ? 'default' : 'secondary'}>
                    {cm.status}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        ) : (
          <EmptyState
            icon={<CreditCardIcon width={48} height={48} className="text-gray-400" />}
            title={t('subscriptions.noActive')}
            description={t('subscriptions.noActiveDesc')}
          />
        )}
      </div>
    </ClientLayout>
  );
}
